/* eslint-disable react-hooks/rules-of-hooks */
import {useSelector} from 'react-redux';
import {
  FeatureSettingEnum,
  isFeatureSettingEnabled,
} from '../../redux/slices/featureSettingsSlice';
import {RootState} from '../../redux/store';
import {
  hasAIInvestigatePermission,
  hasAuditReportsPermission,
  hasClientsViewPermission,
  hasCompletedMappedDataReportViewPermission,
  hasCreateDocument,
  hasCreateOrEditCustomFieldsPermission,
  hasCreateOrEditTagFieldsPermission,
  hasCustomeReportViewPermission,
  hasCustomReportUploadPermission,
  hasDocumentPreviewPermission,
  hasDocumentSummaryGenerationPermission,
  hasECIIndexingPermission,
  hasECIPermission,
  hasEditProjectProfilePermission,
  hasExportPermission,
  hasInitiateProcessingPermission,
  hasIntakeReportViewPermission,
  hasInteractiveVolumeReportViewPermission,
  hasInvoiceReportViewPermission,
  hasManageEvidencePermission,
  hasManageSavedSearchesPermission,
  hasOverlayPermission,
  hasProcessingExceptionsReportViewPermission,
  hasProcessingSummaryReportViewPermission,
  hasProducationMetricsReportViewPermission,
  hasProgressReportViewPermission,
  hasProjectTrackerReportViewPermission,
  hasReportBudgetConsumptionPermission,
  hasSavedSearchesPermission,
  hasSavedSearchTemplatesPermission,
  hasSearchesPermission,
  hasSearchTermReportPermission,
  hasTaggingDocumentPermission,
  hasUploadEvidencePermission,
  hasUserSummaryReportViewPermission,
} from './usePermissionSelector';

export enum PAGE_NAME {
  PROJECT_OVERVIEW = 'project_overview',
  ECA_HOME = 'eca_home',
  ECA_OVERVIEW = 'eca_overview',
  ECA_DOC_LIST = 'eca_doc_list',
  ECA_DOC_CONTAINER = 'eca_doc_container',
  ECA_DOC_TOOL_BAR = 'eca_doc_tool_bar',
  INDEX_SEARCH = 'index_search',
  INDEX_SEARCH_TAB = 'index_search_tab',
  SEARCH_AUDIT_REPORT = 'search_audit_report',
  SEARCH_SAVE_PANEL = 'search_save_panel',
  MANAGE_MENU = 'manage_menu',
  INDEX_OVERVIEW_HEADER = 'index_overview_header',
  FIELD_MANAGEMENT = 'field_management',
  STR_SEARCH_TERM = 'str_search_term',
  FIELD_AND_TAG_MANAGEMENT = 'field_and_tag_management',
  REPORTS = 'reports',
  UPLOAD_EVIDENCE_SOURCE = 'upload_evidence_source',
  MANAGE_EVIDENCE_SOURCE = 'manage_evidence_source',
  REPORTS_VIEW = 'reports_view',
  INDEX_MANAGEMENT = 'index_management',
  OVERLAYS_MANAGEMENT = 'overlays_management',
  EXPORT_MANAGEMENT = 'export_management',
  EDIT_PROJECT_PROFILE = 'edit_project_profile',
  PROCESSING_SUMMARY_EXCEPTIONS_TAB = 'processing_summary_exceptions_tab',
}

const usePermissionsByPage = (pageName: PAGE_NAME, projectId?: string) => {
  switch (pageName) {
    case PAGE_NAME.PROJECT_OVERVIEW: {
      const isEvidenceSourceUploadEnabled = useSelector((state: RootState) =>
        isFeatureSettingEnabled(
          state as any,
          FeatureSettingEnum.EvidenceSourceUpload
        )
      );

      return {
        canViewConciergeReports: useSelector((state: RootState) =>
          hasCustomeReportViewPermission(state, projectId)
        ),
        canUploadConciergeReports: useSelector((state: RootState) =>
          hasCustomReportUploadPermission(state, projectId)
        ),
        canViewInvoiceReports: useSelector((state: RootState) =>
          hasInvoiceReportViewPermission(state, projectId)
        ),
        canViewProjectTracker: useSelector((state: RootState) =>
          hasProjectTrackerReportViewPermission(state, projectId)
        ),
        canUploadEvidence:
          useSelector((state: RootState) =>
            hasUploadEvidencePermission(state, projectId)
          ) && isEvidenceSourceUploadEnabled,
        canManageEvidence:
          useSelector((state: RootState) =>
            hasManageEvidencePermission(state, projectId)
          ) && isEvidenceSourceUploadEnabled,
        canInitiateProcessing: useSelector((state: RootState) =>
          hasInitiateProcessingPermission(state, projectId)
        ),
        canReportBudgetConsumption: useSelector((state: RootState) =>
          hasReportBudgetConsumptionPermission(state, projectId)
        ),
        canClientsView: useSelector((state: RootState) =>
          hasClientsViewPermission(state, projectId)
        ),
        canEditProjectProfile: useSelector((state: RootState) =>
          hasEditProjectProfilePermission(state, projectId)
        ),
        canAccessECI: useSelector((state: RootState) =>
          hasECIPermission(state, projectId)
        ),
        canViewInkateReports: useSelector((state: RootState) =>
          hasIntakeReportViewPermission(state, projectId)
        ),
        canViewProgressReports: useSelector((state: RootState) =>
          hasProgressReportViewPermission(state, projectId)
        ),
        canViewProcessingSummaryReports: useSelector((state: RootState) =>
          hasProcessingSummaryReportViewPermission(state, projectId)
        ),
        canViewUserSummaryReports: useSelector((state: RootState) =>
          hasUserSummaryReportViewPermission(state, projectId)
        ),
        canViewProductionMetricsReports: useSelector((state: RootState) =>
          hasProducationMetricsReportViewPermission(state, projectId)
        ),
        canViewCompletedMappedDataReports: useSelector((state: RootState) =>
          hasCompletedMappedDataReportViewPermission(state, projectId)
        ),
      };
    }
    case PAGE_NAME.ECA_HOME:
      return {
        hasSearchTermReportPerm: useSelector(hasSearchTermReportPermission),
        hasAIInvestigatePerm: useSelector(hasAIInvestigatePermission),
        hasECIIndexingPerm: useSelector(hasECIIndexingPermission),
        hasOverlayPerm: useSelector(hasOverlayPermission),
        hasExportPerm: useSelector(hasExportPermission),
      };
    case PAGE_NAME.ECA_OVERVIEW:
    case PAGE_NAME.INDEX_OVERVIEW_HEADER:
      return {
        hasSearchesPerm: useSelector(hasSearchesPermission),
        hasSavedSearchesPerm: useSelector(hasSavedSearchesPermission),
        hasExportPerm: useSelector(hasExportPermission),
        hasTaggingDocumentPerm: useSelector(hasTaggingDocumentPermission),
      };
    case PAGE_NAME.ECA_DOC_LIST:
      return {
        hasDocumentPreviewPerm: useSelector(hasDocumentPreviewPermission),
        hasTaggingDocumentPerm: useSelector(hasTaggingDocumentPermission),
        hasDocumentSummaryGenerationPerm: useSelector(
          hasDocumentSummaryGenerationPermission
        ),
        hasSavedSearchesPerm: useSelector(hasSavedSearchesPermission),
        hasSearchesPerm: useSelector(hasSearchesPermission),
        hasCreateDocument: useSelector(hasCreateDocument),
      };
    case PAGE_NAME.ECA_DOC_CONTAINER:
      return {
        hasTaggingDocumentPerm: useSelector(hasTaggingDocumentPermission),
        hasCreateOrEditTagFieldsPerm: useSelector(
          hasCreateOrEditTagFieldsPermission
        ),
      };
    case PAGE_NAME.ECA_DOC_TOOL_BAR:
      return {
        hasDocumentSummaryGenerationPerm: useSelector(
          hasDocumentSummaryGenerationPermission
        ),
      };
    case PAGE_NAME.INDEX_SEARCH:
      return {
        hasSearchesPerm: useSelector(hasSearchesPermission),
        hasSearchTermReportPerm: useSelector(hasSearchTermReportPermission),
        hasAIInvestigatePerm: useSelector(hasAIInvestigatePermission),
        hasAuditReportsPerm: useSelector(hasAuditReportsPermission),
      };
    case PAGE_NAME.INDEX_SEARCH_TAB:
    case PAGE_NAME.SEARCH_AUDIT_REPORT:
      return {
        hasSavedSearchesPerm: useSelector(hasSavedSearchesPermission),
      };
    case PAGE_NAME.SEARCH_SAVE_PANEL:
      return {
        hasSavedSearchTemplatesPerm: useSelector(
          hasSavedSearchTemplatesPermission
        ),
      };
    case PAGE_NAME.MANAGE_MENU:
      return {
        hasECIIndexingPerm: useSelector(hasECIIndexingPermission),
        hasOverlayPerm: useSelector(hasOverlayPermission),
        hasExportPerm: useSelector(hasExportPermission),
        hasSavedSearchesPerm: useSelector(hasSavedSearchesPermission),
        hasSavedSearchTemplatesPerm: useSelector(
          hasSavedSearchTemplatesPermission
        ),
        hasManageSavedSearchesPerm: useSelector(
          hasManageSavedSearchesPermission
        ),
        hasCreateOrEditCustomFieldsPerm: useSelector(
          hasCreateOrEditCustomFieldsPermission
        ),
        hasCreateOrEditTagFieldsPerm: useSelector(
          hasCreateOrEditTagFieldsPermission
        ),
      };
    case PAGE_NAME.FIELD_MANAGEMENT:
      return {
        hasAuditReportsPerm: useSelector(hasAuditReportsPermission),
        hasCreateOrEditTagFieldsPerm: useSelector(
          hasCreateOrEditTagFieldsPermission
        ),
        hasCreateOrEditCustomFieldsPerm: useSelector(
          hasCreateOrEditCustomFieldsPermission
        ),
      };
    case PAGE_NAME.STR_SEARCH_TERM:
      return {
        hasDocumentPreviewPerm: useSelector(hasDocumentPreviewPermission),
        hasTaggingDocumentPerm: useSelector(hasTaggingDocumentPermission),
      };
    case PAGE_NAME.FIELD_AND_TAG_MANAGEMENT:
      return {
        hasCreateOrEditTagFieldsPerm: useSelector(
          hasCreateOrEditTagFieldsPermission
        ),
        hasCreateOrEditCustomFieldsPerm: useSelector(
          hasCreateOrEditCustomFieldsPermission
        ),
      };
    case PAGE_NAME.REPORTS:
      return {
        canViewInkateReports: useSelector(hasIntakeReportViewPermission),
        canViewProgressReports: useSelector(hasProgressReportViewPermission),
        canViewProcessingSummaryReports: useSelector(
          hasProcessingSummaryReportViewPermission
        ),
        canViewInteractiveVolumeReports: useSelector(
          hasInteractiveVolumeReportViewPermission
        ),
        canViewUserSummaryReports: useSelector(
          hasUserSummaryReportViewPermission
        ),
        canViewProducationMetricsReports: useSelector(
          hasProducationMetricsReportViewPermission
        ),
        canViewConciergeReports: useSelector(hasCustomeReportViewPermission),
        canViewInvoiceReports: useSelector(hasInvoiceReportViewPermission),
        canViewProjectTracker: useSelector(
          hasProjectTrackerReportViewPermission
        ),
        canViewCompletedMappedDataReports: useSelector(
          hasCompletedMappedDataReportViewPermission
        ),
        canViewProcessingExceptionReports: useSelector(
          hasProcessingExceptionsReportViewPermission
        ),
      };
    case PAGE_NAME.UPLOAD_EVIDENCE_SOURCE: {
      const isEvidenceSourceUploadEnabled = useSelector((state: RootState) =>
        isFeatureSettingEnabled(
          state as any,
          FeatureSettingEnum.EvidenceSourceUpload
        )
      );
      return {
        canInitiateProcessing: useSelector((state: RootState) =>
          hasInitiateProcessingPermission(state, projectId)
        ),
        canManageEvidence:
          useSelector((state: RootState) =>
            hasManageEvidencePermission(state, projectId)
          ) && isEvidenceSourceUploadEnabled,
      };
    }
    case PAGE_NAME.MANAGE_EVIDENCE_SOURCE: {
      return {
        canViewProgressReports: useSelector((state: RootState) =>
          hasProgressReportViewPermission(state, projectId)
        ),
        canViewProcessingSummaryReports: useSelector((state: RootState) =>
          hasProcessingSummaryReportViewPermission(state, projectId)
        ),
        canViewCompletedMappedDataReports: useSelector((state: RootState) =>
          hasCompletedMappedDataReportViewPermission(state, projectId)
        ),
        canInitiateProcessing: useSelector((state: RootState) =>
          hasInitiateProcessingPermission(state, projectId)
        ),
      };
    }
    case PAGE_NAME.REPORTS_VIEW:
      return {
        canUploadConciergeReports: useSelector((state: RootState) =>
          hasCustomReportUploadPermission(state, projectId)
        ),
      };
    case PAGE_NAME.INDEX_MANAGEMENT:
      return {
        hasECIIndexingPerm: useSelector(hasECIIndexingPermission),
      };
    case PAGE_NAME.OVERLAYS_MANAGEMENT:
      return {
        hasOverlayPerm: useSelector(hasOverlayPermission),
      };
    case PAGE_NAME.EXPORT_MANAGEMENT:
      return {
        hasExportPerm: useSelector(hasExportPermission),
      };
    case PAGE_NAME.EDIT_PROJECT_PROFILE:
      return {
        hasEditProjectProfilePerm: useSelector((state: RootState) =>
          hasEditProjectProfilePermission(state, projectId)
        ),
      };
    case PAGE_NAME.PROCESSING_SUMMARY_EXCEPTIONS_TAB:
      return {
        canViewProcessingSummaryReports: useSelector((state: RootState) =>
          hasProcessingSummaryReportViewPermission(state, projectId)
        ),
        canViewProcessingExceptionReports: useSelector(
          hasProcessingExceptionsReportViewPermission
        ),
      };
    default:
      return {};
  }
};

export {usePermissionsByPage};
